import { useEffect } from 'react';
import * as THREE from 'three';
import { useAppStore } from '../store/useAppStore';
import { ALL_PROPS } from '../store/useAppStore';
import { BRUSH_MIN, BRUSH_MAX } from '../paintingConstants';
import { viewportActions } from '../viewportActions';

export interface OpacityScrubRefs {
  opacityDragModeRef: React.MutableRefObject<null | 'brush' | 'layer' | 'size' | 'rotation' | 'blurRadius' | 'fillTolerance'>;
  opacityDragStartValRef: React.MutableRefObject<number>;
  scrubDxRef: React.MutableRefObject<number>;
  lockedReticlePosRef: React.MutableRefObject<{ x: number; y: number }>;
  mouseScreenRef: React.MutableRefObject<{ x: number; y: number }>;
  brushOpacityRef: React.MutableRefObject<number>;
  toolModeRef: React.MutableRefObject<string>;
  stampSizeRef: React.MutableRefObject<number>;
  stampRotationRef: React.MutableRefObject<number>;
  brushSizeRef: React.MutableRefObject<number>;
  blurRadiusRef: React.MutableRefObject<number>;
  cameraRef: React.RefObject<THREE.Camera | null>;
  glRef: React.RefObject<THREE.WebGLRenderer | null>;
  layersRef: React.MutableRefObject<any[]>;
  selectedLayerIdRef: React.MutableRefObject<string | null>;
  lastStampHitPoint: React.MutableRefObject<THREE.Vector3 | null>;
  flattenLayersRef: React.MutableRefObject<() => void>;
  renderTextPreviewRef: React.MutableRefObject<(hitPoint: THREE.Vector3) => void>;
  renderStampPreviewRef: React.MutableRefObject<(hitPoint: THREE.Vector3) => void>;
  setOpacityReticle: React.Dispatch<React.SetStateAction<{ x: number; y: number; value: number; label: string; ringPx?: number; isSquare?: boolean } | null>>;
  fillToleranceRef: React.MutableRefObject<number>;
  fillPreviewUpdateRef: React.MutableRefObject<() => void>;
}

/** Returns the display label for the scrub reticle, mirroring the toolbar slider name. */
function getScrubLabel(dragMode: string, toolMode: string): string {
  switch (dragMode) {
    case 'brush':      return toolMode === 'blur' ? 'Strength' : 'Opacity';
    case 'layer':      return 'Layer';
    case 'size':       return 'Size';
    case 'blurRadius':     return 'Radius';
    case 'rotation':       return 'Rotation';
    case 'fillTolerance':  return 'Tolerance';
    default:               return dragMode;
  }
}

/**
 * Registers global mouse/keyboard listeners for the opacity/size/rotation
 * pointer-lock scrub (A, L, B, R keys). Uses only refs — safe to run once at mount.
 */
export function useOpacityScrub(refs: OpacityScrubRefs): void {
  const {
    opacityDragModeRef,
    opacityDragStartValRef,
    scrubDxRef,
    lockedReticlePosRef,
    mouseScreenRef,
    brushOpacityRef,
    toolModeRef,
    stampSizeRef,
    stampRotationRef,
    brushSizeRef,
    blurRadiusRef,
    cameraRef,
    glRef,
    layersRef,
    selectedLayerIdRef,
    lastStampHitPoint,
    flattenLayersRef,
    renderTextPreviewRef,
    renderStampPreviewRef,
    setOpacityReticle,
    fillToleranceRef,
    fillPreviewUpdateRef,
  } = refs;

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const onMouseMove = (e: MouseEvent) => {
      const mode = opacityDragModeRef.current;
      if (!mode) mouseScreenRef.current = { x: e.clientX, y: e.clientY };
      if (!mode) return;
      scrubDxRef.current += e.movementX;
      const dx = scrubDxRef.current;
      const rx = lockedReticlePosRef.current.x;
      const ry = lockedReticlePosRef.current.y;
      const PX_PER_FULL = 300; // 300 px drag spans 0 → 100%
      if (mode === 'rotation') {
        const isStamp = toolModeRef.current === 'stamp' || toolModeRef.current === 'text';
        if (!isStamp) { opacityDragModeRef.current = null; setOpacityReticle(null); document.exitPointerLock(); return; }
        let rawDeg = opacityDragStartValRef.current + dx / PX_PER_FULL * 360;
        rawDeg = ((rawDeg % 360) + 360) % 360;
        const rotDeg = rawDeg > 180 ? rawDeg - 360 : rawDeg;
        const { setStampRotation } = useAppStore.getState();
        setStampRotation(rotDeg);
        stampRotationRef.current = rotDeg;
        if (lastStampHitPoint.current) {
          if (toolModeRef.current === 'text') renderTextPreviewRef.current(lastStampHitPoint.current);
          else renderStampPreviewRef.current(lastStampHitPoint.current);
        }
        const normVal = (rotDeg + 180) / 360;
        const cam = cameraRef.current as THREE.PerspectiveCamera | null;
        let ringPx: number | undefined;
        if (cam) {
          const fovY = THREE.MathUtils.degToRad(cam.fov);
          const h = glRef.current?.domElement.clientHeight ?? window.innerHeight;
          ringPx = (stampSizeRef.current / Math.tan(fovY / 2)) * (h / 2);
        }
        setOpacityReticle({ x: rx, y: ry, value: normVal, label: getScrubLabel('rotation', toolModeRef.current), ringPx, isSquare: true });
        return;
      }
      if (mode === 'size') {
        const cam = cameraRef.current as THREE.PerspectiveCamera | null;
        if (toolModeRef.current === 'stamp' || toolModeRef.current === 'text') {
          const STAMP_MIN = 0.01, STAMP_MAX = 0.4;
          const range = STAMP_MAX - STAMP_MIN;
          const newSize = Math.max(STAMP_MIN, Math.min(STAMP_MAX, opacityDragStartValRef.current + dx / PX_PER_FULL * range));
          const { setStampSize } = useAppStore.getState();
          setStampSize(newSize);
          stampSizeRef.current = newSize;
          const normVal = (newSize - STAMP_MIN) / range;
          let ringPx: number | undefined;
          if (cam) {
            const fovY = THREE.MathUtils.degToRad(cam.fov);
            const h = glRef.current?.domElement.clientHeight ?? window.innerHeight;
            ringPx = (newSize / Math.tan(fovY / 2)) * (h / 2);
          }
          setOpacityReticle({ x: rx, y: ry, value: normVal, label: getScrubLabel('size', toolModeRef.current), ringPx, isSquare: true });
          return;
        }
        const BRUSH_MIN_L = BRUSH_MIN, BRUSH_MAX_L = BRUSH_MAX;
        const range = BRUSH_MAX_L - BRUSH_MIN_L;
        const newSize = Math.max(BRUSH_MIN_L, Math.min(BRUSH_MAX_L, opacityDragStartValRef.current + dx / PX_PER_FULL * range));
        const { setBrushSize } = useAppStore.getState();
        setBrushSize(newSize);
        brushSizeRef.current = newSize;
        const normVal = (newSize - BRUSH_MIN_L) / range;
        let ringPx: number | undefined;
        if (cam) {
          const fovY = THREE.MathUtils.degToRad(cam.fov);
          const D = cam.position.length();
          const theta = 2 * Math.asin(Math.min(1, newSize / 2));
          const ndcR = Math.sin(theta) / ((D - Math.cos(theta)) * Math.tan(fovY / 2));
          const h = glRef.current?.domElement.clientHeight ?? window.innerHeight;
          ringPx = ndcR * h / 2;
        }
        setOpacityReticle({ x: rx, y: ry, value: normVal, label: getScrubLabel('size', toolModeRef.current), ringPx });
        return;
      }
      if (mode === 'blurRadius') {
        const newRadius = Math.max(0, Math.min(1, opacityDragStartValRef.current + dx / PX_PER_FULL));
        const { setBlurRadius } = useAppStore.getState();
        setBlurRadius(newRadius);
        blurRadiusRef.current = newRadius;
        setOpacityReticle({ x: rx, y: ry, value: newRadius, label: getScrubLabel('blurRadius', toolModeRef.current) });
        return;
      }
      if (mode === 'fillTolerance') {
        const newTol = Math.round(Math.max(0, Math.min(100, opacityDragStartValRef.current + dx / PX_PER_FULL * 100)));
        const { setFillTolerance } = useAppStore.getState();
        setFillTolerance(newTol);
        fillToleranceRef.current = newTol;
        fillPreviewUpdateRef.current();
        setOpacityReticle({ x: rx, y: ry, value: newTol / 100, label: getScrubLabel('fillTolerance', toolModeRef.current) });
        return;
      }
      const newVal = Math.max(0, Math.min(1, opacityDragStartValRef.current + dx / PX_PER_FULL));
      if (mode === 'brush') {
        const { setBrushOpacity } = useAppStore.getState();
        setBrushOpacity(newVal);
      } else {
        const selId = selectedLayerIdRef.current;
        const layer = layersRef.current.find((l: any) => l.id === selId);
        if (layer && selId) {
          layer.opacity = newVal;
          const { updateLayer } = useAppStore.getState();
          updateLayer(selId, { opacity: newVal });
          flattenLayersRef.current();
        }
      }
      setOpacityReticle({ x: rx, y: ry, value: newVal, label: getScrubLabel(mode, toolModeRef.current) });
    };

    const onKeyDown = (e: KeyboardEvent) => {
      if (opacityDragModeRef.current) return;
      const target = e.target as HTMLElement;
      if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA') return;
      if (e.key === 'v' || e.key === 'V') {
        opacityDragModeRef.current = 'brush';
        opacityDragStartValRef.current = brushOpacityRef.current;
        lockedReticlePosRef.current = { ...mouseScreenRef.current };
        scrubDxRef.current = 0;
        glRef.current?.domElement.requestPointerLock();
        setOpacityReticle({ x: mouseScreenRef.current.x, y: mouseScreenRef.current.y, value: brushOpacityRef.current, label: getScrubLabel('brush', toolModeRef.current) });
      } else if (e.key === 'x' || e.key === 'X') {
        const layer = layersRef.current.find((l: any) => l.id === selectedLayerIdRef.current);
        if (!layer) return;
        opacityDragModeRef.current = 'layer';
        opacityDragStartValRef.current = layer.opacity;
        lockedReticlePosRef.current = { ...mouseScreenRef.current };
        scrubDxRef.current = 0;
        glRef.current?.domElement.requestPointerLock();
        setOpacityReticle({ x: mouseScreenRef.current.x, y: mouseScreenRef.current.y, value: layer.opacity, label: getScrubLabel('layer', toolModeRef.current) });
      } else if (e.key === 'c' || e.key === 'C') {
        if (toolModeRef.current === 'blur') {
          opacityDragModeRef.current = 'blurRadius';
          lockedReticlePosRef.current = { ...mouseScreenRef.current };
          scrubDxRef.current = 0;
          glRef.current?.domElement.requestPointerLock();
          opacityDragStartValRef.current = blurRadiusRef.current;
          setOpacityReticle({ x: mouseScreenRef.current.x, y: mouseScreenRef.current.y, value: blurRadiusRef.current, label: getScrubLabel('blurRadius', toolModeRef.current) });
        } else if (toolModeRef.current === 'stamp' || toolModeRef.current === 'text') {
          opacityDragModeRef.current = 'rotation';
          opacityDragStartValRef.current = stampRotationRef.current;
          lockedReticlePosRef.current = { ...mouseScreenRef.current };
          scrubDxRef.current = 0;
          glRef.current?.domElement.requestPointerLock();
          const normVal = (stampRotationRef.current + 180) / 360;
          const cam = cameraRef.current as THREE.PerspectiveCamera | null;
          let ringPx: number | undefined;
          if (cam) {
            const fovY = THREE.MathUtils.degToRad(cam.fov);
            const h = glRef.current?.domElement.clientHeight ?? window.innerHeight;
            ringPx = (stampSizeRef.current / Math.tan(fovY / 2)) * (h / 2);
          }
          setOpacityReticle({ x: mouseScreenRef.current.x, y: mouseScreenRef.current.y, value: normVal, label: getScrubLabel('rotation', toolModeRef.current), ringPx, isSquare: true });
        }
      } else if (e.key === 'b' || e.key === 'B') {
        if (toolModeRef.current === 'fill') {
          opacityDragModeRef.current = 'fillTolerance';
          lockedReticlePosRef.current = { ...mouseScreenRef.current };
          scrubDxRef.current = 0;
          glRef.current?.domElement.requestPointerLock();
          opacityDragStartValRef.current = fillToleranceRef.current;
          setOpacityReticle({ x: mouseScreenRef.current.x, y: mouseScreenRef.current.y, value: fillToleranceRef.current / 100, label: getScrubLabel('fillTolerance', toolModeRef.current) });
        } else {
        opacityDragModeRef.current = 'size';
        lockedReticlePosRef.current = { ...mouseScreenRef.current };
        scrubDxRef.current = 0;
        glRef.current?.domElement.requestPointerLock();
        const isStamp = toolModeRef.current === 'stamp' || toolModeRef.current === 'text';
        if (isStamp) {
          const STAMP_MIN = 0.01, STAMP_MAX = 0.4;
          opacityDragStartValRef.current = stampSizeRef.current;
          const normVal = (stampSizeRef.current - STAMP_MIN) / (STAMP_MAX - STAMP_MIN);
          const cam = cameraRef.current as THREE.PerspectiveCamera | null;
          let ringPx: number | undefined;
          if (cam) {
            const fovY = THREE.MathUtils.degToRad(cam.fov);
            const h = glRef.current?.domElement.clientHeight ?? window.innerHeight;
            ringPx = (stampSizeRef.current / Math.tan(fovY / 2)) * (h / 2);
          }
          setOpacityReticle({ x: mouseScreenRef.current.x, y: mouseScreenRef.current.y, value: normVal, label: getScrubLabel('size', toolModeRef.current), ringPx, isSquare: true });
        } else {
          const BRUSH_MIN_L = BRUSH_MIN, BRUSH_MAX_L = BRUSH_MAX;
          opacityDragStartValRef.current = brushSizeRef.current;
          const normVal = (brushSizeRef.current - BRUSH_MIN_L) / (BRUSH_MAX_L - BRUSH_MIN_L);
          setOpacityReticle({ x: mouseScreenRef.current.x, y: mouseScreenRef.current.y, value: normVal, label: getScrubLabel('size', toolModeRef.current) });
        }
        } // end else (non-fill) branch
      } else if (e.key === 's' || e.key === 'S') {
        const { setKeyframe: sk, currentFrame: frame } = useAppStore.getState();
        const selId = selectedLayerIdRef.current;
        const layer = layersRef.current.find((l: any) => l.id === selId);
        if (!selId || !layer) return;
        viewportActions.pushSnapshot.current();
        const vals: Record<string, boolean | number> = {
          visible: layer.visible,
          opacity: layer.opacity,
          rotateX: layer.rotate.x,
          rotateY: layer.rotate.y,
          rotateZ: layer.rotate.z,
          translateX: layer.translate.x,
          translateY: layer.translate.y,
        };
        ALL_PROPS.forEach((prop) => sk(selId, prop, frame, vals[prop] as boolean | number));
      } else if (!e.ctrlKey && !e.metaKey && (e.key === 'q' || e.key === 'Q')) {
        useAppStore.getState().setToolMode('draw');
      } else if (!e.ctrlKey && !e.metaKey && (e.key === 'w' || e.key === 'W')) {
        useAppStore.getState().setToolMode('fill');
      } else if (!e.ctrlKey && !e.metaKey && (e.key === 'e' || e.key === 'E')) {
        useAppStore.getState().setToolMode('erase');
      } else if (!e.ctrlKey && !e.metaKey && (e.key === 'r' || e.key === 'R')) {
        useAppStore.getState().setToolMode('stamp');
      } else if (!e.ctrlKey && !e.metaKey && (e.key === 't' || e.key === 'T')) {
        useAppStore.getState().setToolMode('text');
      } else if (!e.ctrlKey && !e.metaKey && (e.key === 'y' || e.key === 'Y')) {
        useAppStore.getState().setToolMode('blur');
      } else if (!e.ctrlKey && !e.metaKey && (e.key === 'f' || e.key === 'F')) {
        viewportActions.resetCamera.current();
      } else if (!e.ctrlKey && !e.metaKey && (e.key === ',' || e.key === '<')) {
        const s = useAppStore.getState();
        s.setOverlayArchitecture(!s.overlayArchitecture);
      } else if (!e.ctrlKey && !e.metaKey && (e.key === '.' || e.key === '>')) {
        const s = useAppStore.getState();
        s.setOverlaySafety(!s.overlaySafety);
      }
    };

    const onKeyUp = (e: KeyboardEvent) => {
      if ((e.key === 'v' || e.key === 'V') && opacityDragModeRef.current === 'brush') {
        opacityDragModeRef.current = null; setOpacityReticle(null); document.exitPointerLock();
      } else if ((e.key === 'c' || e.key === 'C') && (opacityDragModeRef.current === 'rotation' || opacityDragModeRef.current === 'blurRadius')) {
        opacityDragModeRef.current = null; setOpacityReticle(null); document.exitPointerLock();
      } else if ((e.key === 'b' || e.key === 'B') && (opacityDragModeRef.current === 'size' || opacityDragModeRef.current === 'fillTolerance')) {
        opacityDragModeRef.current = null; setOpacityReticle(null); document.exitPointerLock();
      } else if ((e.key === 'x' || e.key === 'X') && opacityDragModeRef.current === 'layer') {
        opacityDragModeRef.current = null; setOpacityReticle(null); document.exitPointerLock();
        const { autoKey: ak, setKeyframe: sk, currentFrame: frame, layerKeyframes: kfs } = useAppStore.getState();
        const selId = selectedLayerIdRef.current;
        const layer = layersRef.current.find((l: any) => l.id === selId);
        if (ak !== 0 && selId && layer) {
          if (ak === 2 || (kfs[selId] && Object.keys(kfs[selId].opacity ?? {}).length > 0)) {
            viewportActions.pushSnapshot.current();
            sk(selId, 'opacity', frame, layer.opacity);
          }
        }
      }
    };

    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('keyup', onKeyUp);
    return () => {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('keyup', onKeyUp);
    };
  }, []);
}
